import { type FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Node as RFNode } from 'reactflow';
import type { NodeData } from '@/types/node.types';
import { NODE_TYPE_COLORS, NODE_STATUS_LABELS, NodeType } from '@/types/node.types';
import { useCanvas } from '@/hooks/useCanvas';
import { useCanvasStore } from '@/stores/canvas.store';
import { LinkType } from '@/types/link.types';
import { computeAutoLayout } from '@/services/autoLayout';
import nodesApi from '@/api/nodes.api';
import { exportToJSON } from '@/services/exportCanvas';
import CommentsPanel from '@/components/canvas/controls/CommentsPanel';
import HistoryPanel from '@/components/canvas/controls/HistoryPanel';
import NodeSheet from './NodeSheet';
import AddNodeSheet from './AddNodeSheet';
import MobileMoreMenu from './MobileMoreMenu';

interface MobileCanvasProps {
  canvasId: string;
  workspaceId?: string;
}

type ActiveView = 'tree' | 'list' | 'comments';

const ICONS: Record<string, string> = {
  Vision: '★',
  Objective: '◎',
  Activity: '⚡',
  Risk: '⚠',
  Opportunity: '✨',
  Resource: '⚙',
  KPI: '📊',
};

const TYPE_LABELS: Record<string, string> = {
  Vision: 'Vision',
  Objective: 'Objectif',
  Activity: 'Activité',
  Risk: 'Risque',
  Opportunity: 'Opportunité',
  Resource: 'Ressource',
  KPI: 'KPI',
};

export const MobileCanvas: FC<MobileCanvasProps> = ({ canvasId, workspaceId }) => {
  const { isLoading, error, createNode } = useCanvas(canvasId);
  const canvas = useCanvasStore((s) => s.canvas);
  const nodes = useCanvasStore((s) => s.nodes);
  const edges = useCanvasStore((s) => s.edges);
  const navigate = useNavigate();

  const [view, setView] = useState<ActiveView>('tree');
  const [selectedNode, setSelectedNode] = useState<RFNode<NodeData> | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [commentsNodeId, setCommentsNodeId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Build a simple tree from Hierarchy edges for the tree view
  const tree = useMemo(() => {
    const flowNodes = nodes.filter((n) => n.type !== 'group');
    const childrenMap = new Map<string, string[]>();
    const hasParent = new Set<string>();
    for (const edge of edges) {
      if (edge.data && (edge.data as { linkType?: LinkType }).linkType !== LinkType.Hierarchy) {
        continue;
      }
      const list = childrenMap.get(edge.source) ?? [];
      list.push(edge.target);
      childrenMap.set(edge.source, list);
      hasParent.add(edge.target);
    }
    const roots = flowNodes.filter((n) => !hasParent.has(n.id));
    const visited = new Set<string>();

    interface TreeItem {
      node: RFNode<NodeData>;
      children: TreeItem[];
    }
    const build = (id: string): TreeItem | null => {
      if (visited.has(id)) return null;
      visited.add(id);
      const node = flowNodes.find((n) => n.id === id);
      if (!node) return null;
      const childIds = childrenMap.get(id) ?? [];
      return {
        node,
        children: childIds.map(build).filter((x): x is TreeItem => x !== null),
      };
    };
    const items = roots.map((r) => build(r.id)).filter((x): x is TreeItem => x !== null);
    // Orphans (cycles / detached)
    for (const n of flowNodes) {
      if (!visited.has(n.id)) items.push({ node: n, children: [] });
    }
    return items;
  }, [nodes, edges]);

  const flatList = useMemo(
    () => nodes.filter((n) => n.type !== 'group'),
    [nodes],
  );

  const handleCreateNode = useCallback(
    async (type: NodeType, title: string) => {
      // Place roughly near the existing centroid so auto-layout groups it nicely later
      await createNode(type, { x: 400, y: 200 });
      // createNode uses a default title from the type; we patch it immediately.
      // Find the freshly-added node and patch its title.
      const latest = useCanvasStore.getState().nodes.slice(-1)[0];
      if (latest) {
        try {
          const updated = await nodesApi.update(latest.id, { title });
          useCanvasStore.setState((state) => ({
            nodes: state.nodes.map((n) =>
              n.id === latest.id ? { ...n, data: { ...n.data, ...updated } } : n,
            ),
          }));
        } catch {
          // non-blocking
        }
      }
    },
    [createNode],
  );

  const handleAutoLayout = useCallback(async () => {
    if (nodes.length === 0) return;
    try {
      const positions = await computeAutoLayout(nodes, edges, { direction: 'TB' });
      const repositioned = nodes.map((n) => {
        const p = positions.get(n.id);
        return p ? { ...n, position: p } : n;
      });
      useCanvasStore.setState({ nodes: repositioned });
      positions.forEach((p, id) => {
        nodesApi.update(id, { positionX: p.x, positionY: p.y }).catch(() => {});
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[autoLayout] ELK failed', err);
    }
  }, [nodes, edges]);

  const handleExport = useCallback(() => {
    if (!canvas) return;
    // On mobile we only offer JSON (PNG/PDF require the canvas DOM which isn't rendered)
    exportToJSON(canvas, nodes, edges);
  }, [canvas, nodes, edges]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--color-text-muted)' }}>
        Chargement…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--color-danger)' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 12px',
          borderBottom: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => navigate(workspaceId ? `/workspaces/${workspaceId}` : '/dashboard')}
          style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--color-text)', padding: 4 }}
          aria-label="Retour"
        >
          ←
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Canvas
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {canvas?.name ?? '—'}
          </div>
        </div>
        <button
          onClick={() => setMoreOpen(true)}
          style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--color-text)', padding: 4 }}
          aria-label="Plus"
        >
          ⋮
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {view === 'tree' && (
          <TreeList items={tree} depth={0} onSelect={setSelectedNode} />
        )}
        {view === 'list' && (
          <FlatList nodes={flatList} onSelect={setSelectedNode} />
        )}
        {view === 'comments' && (
          <div style={{ padding: 16, color: 'var(--color-text-muted)', fontSize: 14, lineHeight: 1.5 }}>
            Sélectionne un noeud (dans Arbre ou Liste) puis « Voir les commentaires » pour y accéder.
            Les commentaires sont attachés à un noeud, pas au canvas.
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => setAddOpen(true)}
        aria-label="Ajouter un noeud"
        style={{
          position: 'absolute',
          right: 18,
          bottom: 78,
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: 'var(--color-primary)',
          color: '#fff',
          border: 'none',
          fontSize: 28,
          fontWeight: 300,
          boxShadow: '0 6px 20px rgba(139,92,246,0.45)',
          zIndex: 50,
        }}
      >
        +
      </button>

      {/* Bottom nav */}
      <div
        style={{
          display: 'flex',
          borderTop: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
          flexShrink: 0,
        }}
      >
        <NavTab icon="🌳" label="Arbre" active={view === 'tree'} onClick={() => setView('tree')} />
        <NavTab icon="▤" label="Liste" active={view === 'list'} onClick={() => setView('list')} />
        <NavTab icon="💬" label="Commentaires" active={view === 'comments'} onClick={() => setView('comments')} />
        <NavTab icon="⋯" label="Plus" active={false} onClick={() => setMoreOpen(true)} />
      </div>

      {/* Bottom sheets / overlays */}
      <NodeSheet
        node={selectedNode}
        onClose={() => setSelectedNode(null)}
        onOpenComments={(id) => {
          setSelectedNode(null);
          setCommentsNodeId(id);
        }}
      />
      <AddNodeSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={handleCreateNode}
      />
      <MobileMoreMenu
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onAutoLayout={handleAutoLayout}
        onExport={handleExport}
        onHistory={() => setHistoryOpen(true)}
      />
      <CommentsPanel
        nodeId={commentsNodeId}
        workspaceId={workspaceId ?? ''}
        onClose={() => setCommentsNodeId(null)}
      />
      <HistoryPanel
        canvasId={canvasId}
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onSnapshotRestored={() => {
          setHistoryOpen(false);
          window.location.reload();
        }}
      />
    </div>
  );
};

// ---- helpers ----

const NavTab: FC<{
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      padding: '10px 4px 12px',
      backgroundColor: 'transparent',
      border: 'none',
      color: active ? 'var(--color-primary)' : 'var(--color-text-muted)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      fontSize: 11,
      fontWeight: 600,
    }}
  >
    <span style={{ fontSize: 20 }}>{icon}</span>
    {label}
  </button>
);

const TreeList: FC<{
  items: Array<{ node: RFNode<NodeData>; children: Array<{ node: RFNode<NodeData>; children: unknown[] }> }>;
  depth: number;
  onSelect: (n: RFNode<NodeData>) => void;
}> = ({ items, depth, onSelect }) => {
  if (items.length === 0 && depth === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>
        <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>🌱</div>
        <div style={{ fontSize: 15, fontWeight: 600 }}>Canvas vide</div>
        <div style={{ fontSize: 13, marginTop: 6 }}>Appuie sur <strong style={{ color: 'var(--color-primary)' }}>+</strong> pour ajouter un premier noeud.</div>
      </div>
    );
  }
  return (
    <div>
      {items.map((it) => (
        <TreeRow key={it.node.id} item={it} depth={depth} onSelect={onSelect} />
      ))}
    </div>
  );
};

const TreeRow: FC<{
  item: { node: RFNode<NodeData>; children: Array<{ node: RFNode<NodeData>; children: unknown[] }> };
  depth: number;
  onSelect: (n: RFNode<NodeData>) => void;
}> = ({ item, depth, onSelect }) => {
  const color = NODE_TYPE_COLORS[item.node.data.type] || '#6B7280';
  return (
    <>
      <button
        onClick={() => onSelect(item.node)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '14px 16px',
          paddingLeft: 16 + depth * 16,
          backgroundColor: 'transparent',
          border: 'none',
          borderBottom: '1px solid var(--color-border)',
          textAlign: 'left',
          color: 'var(--color-text)',
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            backgroundColor: color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {ICONS[item.node.data.type] ?? '●'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.node.data.title}
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
            {TYPE_LABELS[item.node.data.type]} · P{item.node.data.priority} · {NODE_STATUS_LABELS[item.node.data.status]}
          </div>
        </div>
        <span style={{ color: 'var(--color-text-muted)', fontSize: 16 }}>›</span>
      </button>
      {item.children.map((c) => (
        <TreeRow
          key={c.node.id}
          item={c as { node: RFNode<NodeData>; children: Array<{ node: RFNode<NodeData>; children: unknown[] }> }}
          depth={depth + 1}
          onSelect={onSelect}
        />
      ))}
    </>
  );
};

const FlatList: FC<{
  nodes: RFNode<NodeData>[];
  onSelect: (n: RFNode<NodeData>) => void;
}> = ({ nodes, onSelect }) => {
  const [filter, setFilter] = useState('');
  const filtered = filter
    ? nodes.filter((n) => n.data.title.toLowerCase().includes(filter.toLowerCase()))
    : nodes;
  return (
    <>
      <div style={{ padding: 12, borderBottom: '1px solid var(--color-border)', position: 'sticky', top: 0, backgroundColor: 'var(--color-background)', zIndex: 1 }}>
        <input
          placeholder="Rechercher…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 14px',
            fontSize: 14,
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            outline: 'none',
          }}
        />
      </div>
      {filtered.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>
          Aucun résultat
        </div>
      ) : (
        filtered.map((n) => (
          <TreeRow
            key={n.id}
            item={{ node: n, children: [] }}
            depth={0}
            onSelect={onSelect}
          />
        ))
      )}
    </>
  );
};

export default MobileCanvas;
